/**
 * heatmapCellEvents — pure helpers that reverse the HeatmapTable bucketing so a
 * clicked heatmap cell (hour slot + quarter) can resolve the raw events that
 * landed in it. These replicate the slot-index bucketing math in
 * src/ui/components/HeatmapTable.js (timestampsToHeatmapRows /
 * completeEventsToDriverHeatmapRows) exactly:
 *
 *   offsetMs  = timestamp * 1000 - startDateMs
 *   reject if offsetMs < 0 || offsetMs >= numHours * 3600000
 *   totalMins = floor(offsetMs / 60000)
 *   hourIndex = floor(totalMins / 60)        // SLOT index, not hour-of-day
 *   quarter   = floor((totalMins % 60) / 15) // 0..3
 *
 * hourIndex is a slot index relative to startDateMs — NOT an hour-of-day — so a
 * non-zero start window maps slot 0 to the window's first hour.
 */

/**
 * Return the events whose timestamp falls in the given heatmap slot/quarter.
 * @param {Array<{timestamp:number}>} events - raw events (timestamp in seconds)
 * @param {number} hourIndex - slot index relative to startDateMs
 * @param {number|null} quarter - 0..3, or null for the whole hour slot
 * @param {number} startDateMs - window start in epoch ms
 * @param {number} numHours - number of hour slots in the window
 * @returns {Array} the matching events (empty array when none/null input)
 */
export function eventsInBucket(events, hourIndex, quarter, startDateMs, numHours) {
    if (!events || events.length === 0) return [];
    const windowMs = numHours * 3600000;
    const matches = [];
    for (const event of events) {
        const offsetMs = event.timestamp * 1000 - startDateMs;
        if (offsetMs < 0 || offsetMs >= windowMs) continue;
        const totalMins = Math.floor(offsetMs / 60000);
        const hIdx = Math.floor(totalMins / 60);
        const q = Math.floor((totalMins % 60) / 15);
        if (hIdx < 0 || hIdx >= numHours) continue;
        if (hIdx === hourIndex && (quarter === null || q === quarter)) {
            matches.push(event);
        }
    }
    return matches;
}

/**
 * Reduce the events in a bucket to one row per unique userId (events without a
 * userId are skipped, mirroring the `if (!event.userId) continue` driver rule
 * in completeEventsToDriverHeatmapRows).
 * @param {Array<{timestamp:number,userId?:string}>} events
 * @param {number} hourIndex - slot index relative to startDateMs
 * @param {number|null} quarter - 0..3, or null for the whole hour slot
 * @param {number} startDateMs - window start in epoch ms
 * @param {number} numHours - number of hour slots in the window
 * @returns {Array<{userId:string,count:number,firstTimestamp:number,events:Array}>}
 */
export function bucketDriverRows(events, hourIndex, quarter, startDateMs, numHours) {
    const inBucket = eventsInBucket(events, hourIndex, quarter, startDateMs, numHours);
    const byUser = new Map();
    for (const event of inBucket) {
        if (!event.userId) continue;
        let row = byUser.get(event.userId);
        if (!row) {
            row = { userId: event.userId, count: 0, firstTimestamp: event.timestamp, events: [] };
            byUser.set(event.userId, row);
        }
        row.count++;
        row.events.push(event);
        if (event.timestamp < row.firstTimestamp) row.firstTimestamp = event.timestamp;
    }
    return [...byUser.values()];
}

/**
 * One `kind:'move'` row per CREATE in the bucket (bucket-by-create-ts). Each row
 * pulls its execution detail from the PDS-surfaced moveDetailByCreateId map; a
 * create with no detail entry (should not happen) yields a pending-like blank row
 * so the row count always equals creates-in-bucket (= the cell value).
 *
 * @param {Array<{eventId:string,timestamp:number}>} createEvents
 * @param {Map<string, Object>} moveDetailByCreateId - keyed by create.eventId
 * @param {number} hourIndex - slot index relative to startDateMs
 * @param {number|null} quarter - 0..3, or null for the whole hour slot
 * @param {number} startDateMs - window start in epoch ms
 * @param {number} numHours - number of hour slots in the window
 * @returns {Array<Object>} move rows (timestamps in seconds)
 */
export function bucketMoveRows(createEvents, moveDetailByCreateId, hourIndex, quarter, startDateMs, numHours) {
    const inBucket = eventsInBucket(createEvents, hourIndex, quarter, startDateMs, numHours);
    return inBucket.map(create => {
        const d = moveDetailByCreateId?.get(create.eventId);
        if (!d) {
            return {
                kind: 'move', status: 'pending',
                // No driver yet; the create's user is who created the move.
                userId: '', createdBy: create.userId || '', vehicleNumber: create.vehicleNumber || '',
                fromLocation: '', toLocation: '',
                startTs: null, completeTs: null, duration: null, cancelTime: null,
                bucketTs: create.timestamp, eventId: create.eventId,
            };
        }
        return {
            kind: 'move', status: d.status,
            userId: d.userId || create.userId || '',
            createdBy: create.userId || '',          // the CREATE event's user = who created the move
            vehicleNumber: d.vehicleNumber || create.vehicleNumber || '',
            fromLocation: d.fromLocation || '',
            toLocation: d.toLocation || '',
            startTs: d.start?.timestamp ?? null,
            completeTs: d.complete?.timestamp ?? null,
            duration: d.duration ?? null,
            cancelTime: d.cancelTime ?? null,
            bucketTs: create.timestamp,
            eventId: create.eventId,
        };
    });
}

/**
 * One `kind:'move'` row per COMPLETE in the bucket, each resolved to its pair's
 * full detail, `status` always `'completed'`. An unpaired complete still renders
 * a row (never dropped) with blank From/Start/Duration so the row count equals
 * completes-in-bucket (= the cell value).
 *
 * @param {Array<{eventId:string,timestamp:number}>} completeEvents
 * @param {Map<string, {create,start,complete}>} completePairByEventId - keyed by complete.eventId
 * @param {number} hourIndex
 * @param {number|null} quarter
 * @param {number} startDateMs
 * @param {number} numHours
 * @returns {Array<Object>} move rows
 */
export function bucketCompleteRows(completeEvents, completePairByEventId, hourIndex, quarter, startDateMs, numHours) {
    const inBucket = eventsInBucket(completeEvents, hourIndex, quarter, startDateMs, numHours);
    return inBucket.map(complete => {
        const p = completePairByEventId?.get(complete.eventId);
        const start = p?.start;
        return {
            kind: 'move', status: 'completed',
            userId: complete.userId || start?.userId || '',
            createdBy: p?.create?.userId || '',
            vehicleNumber: start?.vehicleNumber || complete.vehicleNumber || '',
            fromLocation: start?.location || '',
            toLocation: complete.location || '',
            startTs: start?.timestamp ?? null,
            completeTs: complete.timestamp,
            duration: (start && complete) ? complete.timestamp - start.timestamp : null,
            cancelTime: null,
            bucketTs: complete.timestamp,
            eventId: complete.eventId,
        };
    });
}

// Andon lifecycle prefixes, longest-first is irrelevant — we match the WHOLE type exactly
// as `${prefix}_${suffix}` (never endsWith), so DOCK_DOOR_ASSISTANCE cannot shadow
// SEAL_CUTTING_AND_DOCK_DOOR_ASSISTANCE (F7). This mirrors the exact-type convention the
// series filters already use (e.g. `e.eventType === \`COMPLETE_${suffix}\``).
const ANDON_PREFIXES = ['CREATE', 'START', 'COMPLETE', 'CANCEL', 'ABANDON'];

/**
 * Resolve an andon eventType into its { prefix, suffix } by exact match against the known
 * ANDON_PREFIXES × suffixes grid. Returns null when the type is not a known andon lifecycle
 * event. Exact matching (not endsWith) prevents suffix shadowing (F7).
 * @param {string} eventType
 * @param {string[]} suffixes - ANDON_SUFFIXES
 * @returns {{prefix:string, suffix:string}|null}
 */
function resolveAndon(eventType, suffixes) {
    if (!eventType) return null;
    for (const prefix of ANDON_PREFIXES) {
        if (!eventType.startsWith(`${prefix}_`)) continue;
        const rest = eventType.slice(prefix.length + 1);
        if (suffixes.includes(rest)) return { prefix, suffix: rest };
    }
    return null;
}

/**
 * One `kind:'andon'` row per metric andon event in the bucket. `metricPrefix` is
 * `'CREATE'` for the Andon Create card and `'COMPLETE'` for the Andon Complete card;
 * the metric events are those whose eventType is exactly `${metricPrefix}_<suffix>`.
 * Bucketing is by the metric event's own timestamp.
 *
 * Completion/terminal resolution is anchored to the CLICKED metric event, not to the whole
 * group, so a repeated same-type andon in one visit does not report a different lifecycle's
 * timing (F2):
 *   - COMPLETE card: the clicked event IS the completion; its START is the latest START in
 *     the group at/before it. `completed` is true on the COMPLETE itself even when its START
 *     is out of the fetched window (start/duration blank) — a boundary complete stays
 *     Completed, not Open (F2).
 *   - CREATE card: the completion is the first COMPLETE in the group after this create and
 *     before the next same-group CREATE; its START is the latest START at/before it.
 *   - CANCEL_/ABANDON_ terminal in the relevant interval → status `cancelled`, carrying the
 *     cancel time (F6) — mirrors RealtimeAlertsService treating CANCEL as terminal.
 *
 * The lifecycle is grouped by `suffix:vehicleNumber:visitId` (the RealtimeAlertsService key).
 *
 * Accepted limitation (NIT-1): andonEvents is window-scoped, so on the CREATE card a
 * completion whose START preceded the window resolves with a null START (duration blank)
 * while still showing `completed`.
 *
 * @param {Object[]} andonEvents - full andon lifecycle events
 * @param {'CREATE'|'COMPLETE'} metricPrefix
 * @param {number} hourIndex
 * @param {number|null} quarter
 * @param {number} startDateMs
 * @param {number} numHours
 * @param {string[]} suffixes - ANDON_SUFFIXES, passed in to keep this module import-free
 * @returns {Array<Object>} andon rows
 */
export function bucketAndonRows(andonEvents, metricPrefix, hourIndex, quarter, startDateMs, numHours, suffixes) {
    const sfx = suffixes || [];
    // Group whole lifecycle by suffix:vehicle:visit (mirror RealtimeAlertsService), keeping
    // each group sorted ascending by timestamp so "next create" / "latest start" are cheap.
    const groups = new Map();
    for (const e of (andonEvents || [])) {
        const a = resolveAndon(e.eventType, sfx);
        if (!a) continue;
        const key = `${a.suffix}:${e.vehicleNumber || ''}:${e.visitId || ''}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(e);
    }
    for (const list of groups.values()) list.sort((a, b) => a.timestamp - b.timestamp);

    // Metric events in the bucket (the counted events) — exact `${metricPrefix}_<suffix>`.
    const metric = (andonEvents || []).filter(e => {
        const a = resolveAndon(e.eventType, sfx);
        return a && a.prefix === metricPrefix;
    });
    const inBucket = eventsInBucket(metric, hourIndex, quarter, startDateMs, numHours);

    return inBucket.map(evt => {
        const a = resolveAndon(evt.eventType, sfx);
        const suffix = a?.suffix || '';
        const key = `${suffix}:${evt.vehicleNumber || ''}:${evt.visitId || ''}`;
        const group = groups.get(key) || [evt];

        // Determine the lifecycle interval anchored to the clicked metric event.
        let complete = null;       // the COMPLETE for this metric event's lifecycle
        let terminal = null;       // a CANCEL_/ABANDON_ that closes this lifecycle, if any
        if (metricPrefix === 'COMPLETE') {
            // The clicked event IS the completion.
            complete = evt;
        } else {
            // CREATE card: completion is the first terminal/complete after this create and
            // before the next CREATE in the same group.
            const nextCreate = group.find(e => {
                const r = resolveAndon(e.eventType, sfx);
                return r?.prefix === 'CREATE' && e.timestamp > evt.timestamp;
            });
            const upper = nextCreate ? nextCreate.timestamp : Infinity;
            const inInterval = e => e.timestamp >= evt.timestamp && e.timestamp < upper;
            complete = group.find(e => resolveAndon(e.eventType, sfx)?.prefix === 'COMPLETE' && inInterval(e)) || null;
            if (!complete) {
                terminal = group.find(e => {
                    const p = resolveAndon(e.eventType, sfx)?.prefix;
                    return (p === 'CANCEL' || p === 'ABANDON') && inInterval(e);
                }) || null;
            }
        }

        // For the COMPLETE card, a CANCEL/ABANDON cannot precede a real completion in a
        // sane lifecycle; completion wins. Resolve the START: latest START at/before the
        // completion (or at/before the clicked event when only a terminal exists).
        const upperTs = complete ? complete.timestamp : (terminal ? terminal.timestamp : evt.timestamp);
        const start = group
            .filter(e => resolveAndon(e.eventType, sfx)?.prefix === 'START' && e.timestamp <= upperTs)
            .reduce((latest, e) => (!latest || e.timestamp >= latest.timestamp ? e : latest), null);

        let status;
        if (complete) status = 'completed';
        else if (terminal) status = 'cancelled';
        else status = 'open';

        const completed = status === 'completed';
        const cancelTime = terminal ? terminal.timestamp : null;

        return {
            kind: 'andon',
            status,
            suffix,
            location: evt.location || complete?.location || start?.location || '',
            vehicleNumber: evt.vehicleNumber || '',
            userId: (complete?.userId || start?.userId || evt.userId || ''),
            // Completed rows show START→COMPLETE; a completed row whose START is out of
            // window shows a blank start/duration but stays Completed (F2).
            startTs: completed ? (start?.timestamp ?? null) : null,
            completeTs: completed ? complete.timestamp : null,
            duration: (completed && start) ? complete.timestamp - start.timestamp : null,
            cancelTime,
            bucketTs: evt.timestamp,
            eventId: evt.eventId,
        };
    });
}
